import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NubankVerticalAccordion from "@/components/NubankVerticalAccordion";
import SEOHead from "@/components/SEOHead";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Hook para animar números
const useCountUp = (target: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = target / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  return { count, ref };
};

const ProblemaSolucao = () => {
  const stat1 = useCountUp(59);
  const stat2 = useCountUp(57);
  const stat3 = useCountUp(43);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Por que seu Buffet não Cresce? — AmeUp"
        description="Proposta na mão, contrato no Word, cobrança manual. Veja os 3 problemas que travam seu buffet e como a AmeUp resolve automaticamente."
        canonical="https://ameup.com.br/problema-solucao"
        ogType="website"
        ogTitle="Por que seu Buffet não Cresce? — AmeUp"
        ogDescription="Proposta na mão, contrato no Word, cobrança manual. Veja os 3 problemas que travam seu buffet e como a AmeUp resolve automaticamente."
      />
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-muted">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side - Text content */}
              <div className="space-y-6">
                <div>
                  <Badge className="mb-4 bg-[#eefda3] text-brand-dark border-transparent hover:bg-[#eefda3]/90">
                    O PROBLEMA
                  </Badge>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mt-2">
                    Os 3 problemas que estão travando o crescimento do seu buffet
                  </h1>
                </div>
                <p className="text-lg text-muted-foreground max-w-lg">
                  Não é falta de talento. Não é falta de cliente. É a operação consumindo
                  o tempo que deveria ser gasto no crescimento.
                </p>
              </div>
              
              {/* Right side - Image */}
              <div className="relative">
                <img
                  src="https://qfozcnxbtjjmidhjdepc.supabase.co/storage/v1/object/public/oproblema/close-up-portrait-attractive-young-woman-isolated.jpg"
                  alt="Pessoa estressada organizando evento"
                  className="w-full h-[450px] md:h-[550px] object-cover rounded-3xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Resultados da Pesquisa */}
        <section className="py-12 bg-background">
          <div className="container-custom">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
              E isso vem refletindo negativamente nos seus negócios
            </h2>
            <p className="text-center text-muted-foreground mb-10">
              Em pesquisa realizada com clientes de buffets e empresas de eventos:
            </p>
            
            <div className="max-w-4xl mx-auto space-y-4">
              {/* Primeira linha - 2 cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div ref={stat1.ref} className="bg-background rounded-2xl p-8 shadow-sm border border-border">
                  <div className="text-4xl md:text-5xl font-bold mb-3 text-brand-dark bg-[#eefda3] px-3 py-1 rounded-md inline-block">{stat1.count}%</div>
                  <p className="text-muted-foreground">
                    <span className="font-semibold text-foreground">desistiram de realizar</span> um evento / festa dado a complexidade envolvida.
                  </p>
                </div>
                
                <div ref={stat2.ref} className="bg-background rounded-2xl p-8 shadow-sm border border-border">
                  <div className="text-4xl md:text-5xl font-bold mb-3 text-brand-dark bg-[#eefda3] px-3 py-1 rounded-md inline-block">{stat2.count}%</div>
                  <p className="text-muted-foreground">
                    dos sentimentos apontados são <span className="font-semibold text-foreground">negativos</span>.
                  </p>
                </div>
              </div>
              
              {/* Segunda linha - 1 card full width */}
              <div ref={stat3.ref} className="bg-background rounded-2xl p-8 shadow-sm border border-border">
                <div className="text-4xl md:text-5xl font-bold mb-3 text-brand-dark bg-[#eefda3] px-3 py-1 rounded-md inline-block">{stat3.count}%</div>
                <p className="text-muted-foreground">
                  dos clientes possuem <span className="font-semibold text-foreground">muitas dúvidas</span> de qual fornecedor escolher.
                </p>
              </div>

              {/* Rodapé da pesquisa */}
              <p className="text-center text-sm text-muted-foreground/70 mt-6">
                241 entrevistados · Pesquisa realizada em 2025 · AmeUp · Brasil
              </p>
            </div>
          </div>
        </section>

        {/* Nubank Vertical Accordion */}
        <NubankVerticalAccordion />

        {/* CTA Final */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-[#eefda3] to-[#c8e87a]">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-brand-dark">
                Chega de trabalhar para a operação. Faça ela trabalhar por você.
              </h2>
              <p className="text-base sm:text-lg md:text-xl mb-8 text-brand-dark/70">
                Junte-se aos buffets que já trocaram o caos operacional pelo crescimento.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-brand-dark text-white hover:bg-brand-dark/90 font-semibold">
                  <Link to="/cadastro-fornecedor">Começar grátis agora</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer hidePaymentMethods={true} />
    </div>
  );
};

export default ProblemaSolucao;
